/**
 * The one place the app talks to /api. Same-origin (the dev server proxies),
 * JSON in and out, and errors carry the server's `error` code plus any zod
 * issues so forms can highlight the right field instead of a generic toast.
 */
export interface ApiIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly issues: ApiIssue[] = [],
  ) {
    super(code);
    this.name = "ApiError";
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // no body
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; issues?: ApiIssue[] };
    throw new ApiError(res.status, b.error ?? `http_${res.status}`, b.issues ?? []);
  }
  return body as T;
}

export const api = {
  async get<T>(path: string): Promise<T> {
    return handle<T>(await fetch(path, { headers: { accept: "application/json" }, credentials: "same-origin" }));
  },
  async post<T>(path: string, body: unknown): Promise<T> {
    return handle<T>(
      await fetch(path, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body),
      }),
    );
  },
  async patch<T>(path: string, body: unknown): Promise<T> {
    return handle<T>(
      await fetch(path, {
        method: "PATCH",
        headers: { "content-type": "application/json", accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body),
      }),
    );
  },
  async delete<T>(path: string): Promise<T> {
    return handle<T>(await fetch(path, { method: "DELETE", headers: { accept: "application/json" }, credentials: "same-origin" }));
  },
  /** Multipart with a progress-free fetch (audio notes are small). */
  async postForm<T>(path: string, form: FormData): Promise<T> {
    return handle<T>(await fetch(path, { method: "POST", body: form, credentials: "same-origin" }));
  },
};

/** Fire-and-forget beacon; never awaited by UI, never throws. */
export function beacon(path: string, body: unknown): void {
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    if (typeof navigator !== "undefined" && navigator.sendBeacon?.(path, blob)) return;
    void fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), keepalive: true }).catch(() => {});
  } catch {
    // analytics must never break a share
  }
}
